/* ============================================================
   HEDDZ - cart drawer + upsell engine

   Kept deliberately separate from heddz.js so the buy-box code in that
   file is never at risk from a drawer edit.

   NO PRICE IS HARDCODED IN THIS FILE. Every figure is derived at runtime
   from /cart.js and from the live selling_plan_allocations emitted by
   snippets/buy-box-product-json.liquid.

   COPY RULE: no em dashes in any user-facing string in this file. The offer
   price line is word-for-word identical to priceStory() in heddz.js:
     "<first> on your first order (then <recurring> after)"

   TOTALS ARE DETERMINISTIC. Going through /discount/<code> sets a session
   cookie, after which /cart.js starts reporting the credit itself. Reading
   cart.total_price raw therefore gives a different drawer depending on
   whether the shopper has been to checkout yet. Instead we reconstruct the
   pre-credit base (total_price + total_discount) and subtract the credit
   ourselves, so the drawer shows the same numbers every time and for every
   product.

   ONE CHECKOUT PATH. goToCheckout() is the only place that decides whether to
   route via /discount/<code>. The header cart icon ([data-cart-open]) and the
   /cart page's checkout button ([data-cart-page-checkout]) are both delegated
   here so the discount logic never gets duplicated or forgotten.

   UPSELL RULES (first match wins, evaluated top to bottom):
     1. trio on subscription      -> no upsell ("all set")
     2. six pack on subscription  -> no upsell ("all set")
     3. six pack one-time         -> switch to subscribe & save
     4. trio one-time             -> switch to subscribe & save
     5. any single pod            -> upgrade to trio on subscribe & save,
                                     removing the pod (an upgrade, not an add-on)
     6. otherwise                 -> no offer

   Public API (used by heddz.js):
     window.Heddz.cartDrawer.open()
     window.Heddz.cartDrawer.close()
     window.Heddz.cartDrawer.refresh()
     window.Heddz.cartDrawer.addAndOpen(item)   // item = {id, quantity, selling_plan?}
   ============================================================ */
(function () {
  'use strict';

  var routes = (window.Heddz && window.Heddz.routes) || {};
  var CART_ADD = routes.cartAdd || '/cart/add.js';
  var CART_CHANGE = routes.cartChange || '/cart/change.js';
  var CART_GET = '/cart.js';
  var CHECKOUT_URL = '/checkout';
  var MONEY_FORMAT = (window.Heddz && window.Heddz.moneyFormat) || '${{amount}}';

  var POD_ROLES = ['relief', 'calm', 'focus'];
  var JSON_HEADERS = { 'Content-Type': 'application/json', 'Accept': 'application/json' };

  /* ---------- money ---------- */

  function centsFromMoneyString(str) {
    if (typeof str !== 'string') return null;
    var cleaned = str.replace(/[^0-9.,]/g, '');
    if (!cleaned) return null;
    var m = cleaned.match(/^(.*)([.,])(\d{2})$/);
    if (m) {
      var whole = m[1].replace(/[.,]/g, '');
      return parseInt(whole || '0', 10) * 100 + parseInt(m[3], 10);
    }
    return parseInt(cleaned.replace(/[.,]/g, ''), 10) * 100;
  }

  function group(intPart, sep) {
    return intPart.replace(/(\d)(?=(\d{3})+$)/g, '$1' + sep);
  }

  function money(cents) {
    if (cents == null || isNaN(cents)) return '';
    var n = Math.abs(Math.round(cents));
    var whole = String(Math.floor(n / 100));
    var frac = String(n % 100);
    if (frac.length < 2) frac = '0' + frac;
    return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      switch (token) {
        case 'amount_no_decimals': return group(whole, ',');
        case 'amount_with_comma_separator': return group(whole, '.') + ',' + frac;
        case 'amount_no_decimals_with_comma_separator': return group(whole, '.');
        case 'amount_with_apostrophe_separator': return group(whole, "'") + '.' + frac;
        default: return group(whole, ',') + '.' + frac;
      }
    });
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ---------- buy-box payload (products, plans, intro discount) ---------- */

  var payload = null;

  function getPayload() {
    if (payload !== null) return payload;
    var el = document.querySelector('script[data-buybox-json]');
    if (!el) { payload = false; return payload; }
    try { payload = JSON.parse(el.textContent); } catch (e) { payload = false; }
    return payload;
  }

  function roleMap() {
    var p = getPayload();
    var map = {};
    if (!p || !p.products) return map;
    Object.keys(p.products).forEach(function (role) {
      var v = p.products[role] && p.products[role].variantId;
      if (v) map[String(v)] = role;
    });
    return map;
  }

  function productFor(role) {
    var p = getPayload();
    return (p && p.products && p.products[role]) || null;
  }

  function introConfig() {
    var p = getPayload();
    var i = (p && p.intro) || null;
    if (!i || !i.code || !i.amountCents) return null;
    return { code: i.code, amountCents: i.amountCents, roles: i.roles || [] };
  }

  function planFor(role) {
    var prod = productFor(role);
    if (!prod || !prod.plans || !prod.plans.length) return null;

    var plans = prod.plans;
    var chosen = null;
    for (var i = 0; i < plans.length; i++) {
      if (plans[i].adjustments > 1) { chosen = plans[i]; break; }
    }
    if (!chosen) chosen = plans[0];

    var firstCents = chosen.firstCents != null ? chosen.firstCents : centsFromMoneyString(chosen.firstMoney);
    var recurCents = chosen.recurringCents != null ? chosen.recurringCents : centsFromMoneyString(chosen.recurringMoney);
    if (recurCents == null) recurCents = firstCents;

    var planHasIntro = chosen.adjustments > 1;

    var intro = introConfig();
    var introCents = null;
    if (planHasIntro) {
      introCents = firstCents;
    } else if (intro && intro.roles.indexOf(role) > -1 && firstCents != null) {
      introCents = Math.max(0, firstCents - intro.amountCents);
    }

    return {
      id: chosen.id,
      name: chosen.name,
      recurringCents: recurCents,
      introCents: introCents,
      hasIntro: introCents != null && recurCents != null && introCents < recurCents,
      viaDiscountCode: !planHasIntro && introCents != null
    };
  }

  /* ---------- cart ---------- */

  function readJson(r) {
    return r.json().then(function (data) {
      if (!r.ok) throw data;
      return data;
    });
  }

  function fetchCart() {
    return fetch(CART_GET, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(readJson);
  }

  function changeLine(key, quantity) {
    return fetch(CART_CHANGE, {
      method: 'POST', headers: JSON_HEADERS, credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(readJson);
  }

  function addItem(item) {
    return fetch(CART_ADD, {
      method: 'POST', headers: JSON_HEADERS, credentials: 'same-origin',
      body: JSON.stringify({ items: [item] })
    }).then(readJson);
  }

  function annotate(cart) {
    var map = roleMap();
    return (cart.items || []).map(function (line) {
      var alloc = line.selling_plan_allocation || null;
      return {
        line: line,
        key: line.key,
        role: map[String(line.id)] || null,
        isSub: !!(alloc && alloc.selling_plan && alloc.selling_plan.id),
        planName: alloc && alloc.selling_plan ? alloc.selling_plan.name : null
      };
    });
  }

  /* The header badge is server-rendered, so it would sit at its page-load value
     forever once items are added over AJAX. Keep it in sync on every render. */
  function updateBadge(count) {
    var nodes = document.querySelectorAll('[data-cart-count], .hdr__cart-count');
    Array.prototype.forEach.call(nodes, function (n) {
      n.textContent = count;
      if (count > 0) { n.removeAttribute('hidden'); }
      else { n.setAttribute('hidden', ''); }
    });
  }

  /* ---------- upsell decision ---------- */

  function decide(rows) {
    function find(role, sub) {
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].role === role && rows[i].isSub === sub) return rows[i];
      }
      return null;
    }

    if (find('trio', true) || find('double', true)) return { type: 'won' };

    var sixOnce = find('double', false);
    if (sixOnce && planFor('double')) return { type: 'plan', role: 'double', row: sixOnce };

    var trioOnce = find('trio', false);
    if (trioOnce && planFor('trio')) return { type: 'plan', role: 'trio', row: trioOnce };

    for (var i = 0; i < rows.length; i++) {
      if (POD_ROLES.indexOf(rows[i].role) > -1 && planFor('trio')) {
        return { type: 'upgrade', role: 'trio', row: rows[i] };
      }
    }

    return null;
  }

  function offerCopy(decision) {
    var plan = planFor(decision.role);
    if (!plan) return null;

    var p = getPayload();
    var setg = (p && p.settings) || {};
    var subName = setg.subscribeName || 'subscribe & save';
    var names = setg.bundleNames || {};
    var label = names[decision.role] || decision.role;

    var recur = money(plan.recurringCents);
    var priceLine = plan.hasIntro
      ? '<b>' + money(plan.introCents) + '</b> on your first order (then ' + recur + ' after).'
      : '<b>' + recur + '</b> on every order.';

    if (decision.type === 'upgrade') {
      return {
        kicker: 'upgrade',
        head: 'three intents, nothing wasted',
        body: 'trade up to ' + esc(label) + ': ' + priceLine,
        btn: 'upgrade to ' + label,
        then: 'we swap the single pod out of your cart. skip or cancel anytime.'
      };
    }

    return {
      kicker: subName,
      head: decision.role === 'double' ? 'never run dry' : 'make it a monthly trio',
      body: priceLine,
      btn: 'switch to ' + subName,
      then: 'skip, swap scents, or cancel anytime.'
    };
  }

  function applyOffer(decision) {
    var prod = productFor(decision.role);
    var plan = planFor(decision.role);
    if (!prod || !prod.variantId || !plan) return Promise.reject({ description: 'this upgrade is not available right now.' });

    var qty = decision.row && decision.row.line ? (decision.row.line.quantity || 1) : 1;
    if (decision.type === 'upgrade') qty = 1;

    var removeKey = decision.row ? decision.row.key : null;

    var chain = removeKey ? changeLine(removeKey, 0) : Promise.resolve();
    return chain.then(function () {
      return addItem({ id: prod.variantId, quantity: qty, selling_plan: plan.id });
    });
  }

  /* ---------- rendering ---------- */

  var el = {};
  var lastRows = [];
  var lastDecision = null;

  function q(sel) { return el.root ? el.root.querySelector(sel) : null; }

  function bind() {
    el.root = document.querySelector('[data-cart-drawer]');
    if (!el.root) return false;

    if (el.root.parentNode !== document.body) document.body.appendChild(el.root);

    el.items = q('[data-cd-items]');
    el.upsell = q('[data-cd-upsell]');
    el.urg = q('[data-cd-urg]');
    el.was = q('[data-cd-was]');
    el.now = q('[data-cd-now]');
    el.note = q('[data-cd-note]');
    el.checkout = q('[data-cd-checkout]');
    el.err = q('[data-cd-err]');
    return true;
  }

  function showErr(msg) {
    if (!el.err) return;
    el.err.textContent = msg;
    el.err.hidden = false;
  }
  function clearErr() { if (el.err) el.err.hidden = true; }

  function lineHtml(row) {
    var line = row.line;
    var img = line.image
      ? '<img src="' + esc(line.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2')) + '" alt="">'
      : '';
    var unit = line.final_line_price != null ? line.final_line_price : line.line_price;
    var wasLine = (line.original_line_price != null && line.original_line_price > unit)
      ? '<s>' + money(line.original_line_price) + '</s>' : '';

    return '' +
      '<li class="hd-cd__row">' +
        '<span class="hd-cd__thumb">' + img + '</span>' +
        '<span>' +
          '<span class="hd-cd__nm">' + esc(line.product_title) + '</span>' +
          (row.planName ? '<span class="hd-cd__plan">' + esc(row.planName) + '</span>' : '') +
          '<span class="hd-cd__qtyrow">' +
            '<button type="button" class="hd-cd__qbtn" data-cd-dec="' + esc(row.key) + '" aria-label="decrease quantity"' + (line.quantity <= 1 ? ' disabled' : '') + '>&minus;</button>' +
            '<span class="hd-cd__qn">' + line.quantity + '</span>' +
            '<button type="button" class="hd-cd__qbtn" data-cd-inc="' + esc(row.key) + '" aria-label="increase quantity">+</button>' +
          '</span>' +
          '<button type="button" class="hd-cd__rm" data-cd-rm="' + esc(row.key) + '">remove</button>' +
        '</span>' +
        '<span class="hd-cd__pr">' + wasLine + money(unit) + '</span>' +
      '</li>';
  }

  function render(cart) {
    updateBadge(cart.item_count || 0);
    if (!el.root) return;

    var rows = annotate(cart);
    lastRows = rows;

    if (!rows.length) {
      el.items.innerHTML = '<li class="hd-cd__empty">your cart is empty.</li>';
    } else {
      el.items.innerHTML = rows.map(lineHtml).join('');
    }

    /* Deterministic totals, see the note at the top of this file.
       base   = what the cart costs before any first-order credit
       credit = the first-order credit, whether or not /cart.js knows yet */
    var pending = pendingIntro(rows);
    var alreadyOff = cart.total_discount || 0;
    var base = (cart.total_price || 0) + alreadyOff;
    var credit = pending ? pending.amountCents : alreadyOff;
    var nowCents = Math.max(0, base - credit);

    if (el.now) el.now.textContent = money(nowCents);
    if (el.was) {
      if (base > nowCents) { el.was.textContent = money(base); el.was.hidden = false; }
      else { el.was.hidden = true; }
    }
    if (el.note) {
      if (credit > 0 && rows.length) {
        el.note.textContent = 'includes your ' + money(credit) + ' first-order credit';
        el.note.hidden = false;
      } else {
        el.note.hidden = true;
      }
    }

    var decision = rows.length ? decide(rows) : null;
    lastDecision = decision;

    if (!decision) {
      el.upsell.hidden = true;
      el.upsell.innerHTML = '';
    } else if (decision.type === 'won') {
      el.upsell.hidden = false;
      el.upsell.innerHTML =
        '<div class="hd-cd__won">' +
          '<p class="hd-cd__won-h">you are all set.</p>' +
          '<p class="hd-cd__won-b">your bundle is on subscribe &amp; save. skip, swap scents or cancel anytime.</p>' +
        '</div>';
    } else {
      var copy = offerCopy(decision);
      if (!copy) {
        el.upsell.hidden = true;
        el.upsell.innerHTML = '';
      } else {
        el.upsell.hidden = false;
        el.upsell.innerHTML =
          '<div class="hd-cd__up">' +
            '<div class="hd-cd__up-k">' + esc(copy.kicker) + '</div>' +
            '<p class="hd-cd__up-h">' + esc(copy.head) + '</p>' +
            '<p class="hd-cd__up-b">' + copy.body + '</p>' +
            '<button type="button" class="hd-cd__up-btn" data-cd-take>' + esc(copy.btn) + '</button>' +
            '<p class="hd-cd__up-then">' + esc(copy.then) + '</p>' +
          '</div>';
      }
    }
  }

  /* is there a subscription line eligible for the first-order discount code? */
  function pendingIntro(rows) {
    var intro = introConfig();
    if (!intro) return null;
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      if (!r.isSub || !r.role) continue;
      if (intro.roles.indexOf(r.role) === -1) continue;
      var plan = planFor(r.role);
      if (plan && plan.viaDiscountCode) return intro;
    }
    return null;
  }

  /* ---------- open / close ---------- */

  var lastFocus = null;

  function open() {
    if (!el.root) return;
    lastFocus = document.activeElement;
    el.root.hidden = false;
    requestAnimationFrame(function () { el.root.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
    var closeBtn = q('.hd-cd__x');
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    if (!el.root) return;
    el.root.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function () { if (!el.root.classList.contains('is-open')) el.root.hidden = true; }, 320);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function refresh() {
    return fetchCart().then(function (cart) { render(cart); return cart; });
  }

  function addAndOpen(item) {
    clearErr();
    return addItem(item)
      .then(refresh)
      .then(function (cart) { open(); return cart; })
      .catch(function (e) {
        open();
        showErr((e && e.description) || 'sorry, we couldn’t add that. please try again.');
        throw e;
      });
  }

  /* ---------- events ---------- */

  function closestOf(node, sel) {
    while (node && node !== document) {
      if (node.nodeType === 1 && node.matches && node.matches(sel)) return node;
      node = node.parentNode;
    }
    return null;
  }

  function wire() {
    el.root.addEventListener('click', function (e) {
      var t = e.target;

      if (closestOf(t, '[data-cd-close]')) { e.preventDefault(); close(); return; }

      var inc = closestOf(t, '[data-cd-inc]');
      var dec = closestOf(t, '[data-cd-dec]');
      var rm = closestOf(t, '[data-cd-rm]');
      var take = closestOf(t, '[data-cd-take]');

      if (inc || dec || rm) {
        e.preventDefault();
        clearErr();
        var key = inc ? inc.getAttribute('data-cd-inc')
                : dec ? dec.getAttribute('data-cd-dec')
                : rm.getAttribute('data-cd-rm');
        var row = null;
        for (var i = 0; i < lastRows.length; i++) { if (lastRows[i].key === key) row = lastRows[i]; }
        if (!row) return;
        var qty = rm ? 0 : (inc ? row.line.quantity + 1 : row.line.quantity - 1);
        changeLine(key, qty).then(render).catch(function (err) {
          showErr((err && err.description) || 'could not update your cart.');
        });
        return;
      }

      if (take) {
        e.preventDefault();
        clearErr();
        if (!lastDecision) return;
        take.disabled = true;
        applyOffer(lastDecision)
          .then(refresh)
          .catch(function (err) {
            take.disabled = false;
            showErr((err && err.description) || 'could not apply that upgrade.');
          });
        return;
      }

      if (closestOf(t, '[data-cd-checkout]')) {
        e.preventDefault();
        goToCheckout();
      }
    });

    /* Header cart icon and the /cart page checkout button, delegated at the
       document so they work on every template. */
    document.addEventListener('click', function (e) {
      // let cmd/ctrl/shift/middle clicks fall through to the real href
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button && e.button !== 0)) return;

      if (closestOf(e.target, '[data-cart-open]')) {
        e.preventDefault();
        open();
        refresh().catch(function () {});
        return;
      }

      if (closestOf(e.target, '[data-cart-page-checkout]')) {
        e.preventDefault();
        refresh().then(goToCheckout).catch(goToCheckout);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && el.root && el.root.classList.contains('is-open')) close();
    });
  }

  function goToCheckout() {
    var pending = pendingIntro(lastRows);
    if (el.checkout) el.checkout.disabled = true;
    if (pending) {
      window.location.href = '/discount/' + encodeURIComponent(pending.code) +
        '?redirect=' + encodeURIComponent(CHECKOUT_URL);
    } else {
      window.location.href = CHECKOUT_URL;
    }
  }

  /* ---------- boot ---------- */

  function init() {
    if (!bind()) return;
    wire();
    refresh().catch(function () {});

    window.Heddz = window.Heddz || {};
    window.Heddz.cartDrawer = {
      open: open,
      close: close,
      refresh: refresh,
      addAndOpen: addAndOpen
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', function () { payload = null; refresh().catch(function () {}); });
})();
